import { execute } from '@/graphql/execute';
import { useMe } from '@/hooks/useMe';
import { useOrganization } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { dashboardStatsQuery, sharedLinkDashboardQuery } from '@/queries/dashboard.query';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import LayerStats from './dashboard/LayerStats';
import ProjectsMap from './dashboard/ProjectsMap';
import SharedLinkStats from './dashboard/SharedLinkStats';
import StorageChart from './dashboard/StorageChart';

const Dashboard: FunctionComponent = () => {
  const { t } = useTranslation('common');
  const organization = useOrganization();
  const { me: user } = useMe();
  usePageMeta({ title: [t('dashboard.title')] });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats', organization?.id],
    queryFn: () => execute(dashboardStatsQuery),
    enabled: !!organization?.id,
  });

  const { data: linksData, isLoading: linksLoading } = useQuery({
    queryKey: ['sharedLinkDashboard', organization?.id],
    queryFn: () => execute(sharedLinkDashboardQuery),
    enabled: !!organization?.id,
  });

  const isLoading = statsLoading || linksLoading;
  const linkDash = linksData?.sharedLinkDashboard;

  const hour = new Date().getHours();
  const timeOfDay =
    hour < 5
      ? 'Night'
      : hour < 8
        ? 'EarlyMorning'
        : hour < 10
          ? 'Morning'
          : hour < 12
            ? 'LateMorning'
            : hour < 14
              ? 'Midday'
              : hour < 17
                ? 'Afternoon'
                : hour < 20
                  ? 'EarlyEvening'
                  : hour < 22
                    ? 'Evening'
                    : 'Night';

  return (
    <PageSkeleton steps={[{ label: t('dashboard.title'), href: '/' }]}>
      <div className='flex-1 overflow-auto'>
        {isLoading ? (
          <div className='flex items-center justify-center h-64'>
            <Loader2 className='size-6 animate-spin text-slate-400' />
          </div>
        ) : (
          <div className='p-6 max-w-7xl mx-auto space-y-6'>
            <div>
              <h1 className='text-2xl font-bold'>
                {t(`dashboard.welcomeBack${timeOfDay}`, { name: user?.firstName })}
              </h1>
              <p className='text-slate-500 dark:text-stone-400'>{t('dashboard.welcomeBackDescription')}</p>
            </div>
            <ProjectsMap projects={statsData?.dashboardStats.projects || []} />
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <StorageChart projects={statsData?.dashboardStats.projects || []} />
              <LayerStats
                layerTypeCounts={statsData?.dashboardStats.layerTypeCounts || []}
                totalLayers={statsData?.dashboardStats.totalLayers || 0}
              />
            </div>
            <SharedLinkStats
              dailyViews={linkDash?.dailyViews || []}
              topProjectsByLinks={linkDash?.topProjectsByLinks || []}
              topLinksByViews={linkDash?.topLinksByViews || []}
              totalLinks={linkDash?.totalLinks || 0}
            />
          </div>
        )}
      </div>
    </PageSkeleton>
  );
};

export default Dashboard;
